import type {
  CheckoutMethodsResetResponse,
  CheckoutMethodsResponse,
  CheckoutMethodsUpdateRequest,
} from '../types';
import type { RequestFn } from '../client';

/**
 * A shop's payment-method layout for the hosted checkout: the express strip,
 * the main list, the "other payment methods" list, and each item's overrides
 * (text, translations, icon, look, open target, visibility). One document per
 * shop (business profile), owned by the shop rather than by each connector
 * account. Until a shop saves one, the router derives a layout from the
 * shop's connector accounts and reports `source: 'derived'`.
 *
 * Dashboard JWT (`CheckoutMethods` read / write) or an admin key.
 */
export class CheckoutMethods {
  constructor(private readonly request: RequestFn) {}

  /**
   * The shop's layout (stored, or derived from its accounts) and every item
   * it may place. `GET /checkout-methods/{profile_id}`
   */
  async retrieve(merchantId: string, profileId: string): Promise<CheckoutMethodsResponse> {
    return this.request('GET', `/checkout-methods/${encodeURIComponent(profileId)}`, {
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Replace the shop's layout. The router refuses an express item that does
   * not draw its own button, more than two rows of two in the express strip,
   * a title without text, or an item no account of the shop offers.
   * `PUT /checkout-methods/{profile_id}`
   */
  async update(
    merchantId: string,
    profileId: string,
    body: CheckoutMethodsUpdateRequest,
  ): Promise<CheckoutMethodsResponse> {
    return this.request('PUT', `/checkout-methods/${encodeURIComponent(profileId)}`, {
      query: { merchant_id: merchantId },
      body,
    });
  }

  /**
   * Delete the stored layout; the shop is back on the derived one.
   * `DELETE /checkout-methods/{profile_id}`
   */
  async reset(merchantId: string, profileId: string): Promise<CheckoutMethodsResetResponse> {
    return this.request('DELETE', `/checkout-methods/${encodeURIComponent(profileId)}`, {
      query: { merchant_id: merchantId },
    });
  }
}
